// Turns the filtered data channel (`webrtcfilter=on`) into the few conversation signals the page needs.
// Pure: no DOM, no timers. Transcript text never leaves JavaScript; deltas are dropped entirely.

export type TalkSignal =
    | 'speech-started'
    | 'barge-in'
    | 'speech-stopped'
    | 'output-started'
    | 'output-stopped'
    | 'assistant-transcript-done'
    | 'user-transcript-done';

export interface TalkSignalState {
    // The agent's audio is playing (between output_audio_buffer.started and .stopped/.cleared).
    readonly outputActive: boolean;
}

export interface TalkSignalResult {
    readonly signals: TalkSignal[];
    readonly state: TalkSignalState;
}

export const INITIAL_TALK_SIGNAL_STATE: TalkSignalState = { outputActive: false };

// Maps one data channel message to the signals it raises and the state after it. Anything that is not a
// JSON event with a known `type` raises nothing and leaves the state alone.
export function mapDataChannelMessage(data: unknown, state: TalkSignalState): TalkSignalResult {
    const type = readEventType(data);
    switch (type) {
        case 'input_audio_buffer.speech_started':
            // Speaking over the agent: Azure cancels and clears the answer itself (native barge-in).
            return { signals: state.outputActive ? ['speech-started', 'barge-in'] : ['speech-started'], state };
        case 'input_audio_buffer.speech_stopped':
            return { signals: ['speech-stopped'], state };
        case 'output_audio_buffer.started':
            return { signals: ['output-started'], state: { outputActive: true } };
        case 'output_audio_buffer.stopped':
        case 'output_audio_buffer.cleared':
            // A clear after a natural stop (or a stop after a clear) must not end a second time.
            return state.outputActive
                ? { signals: ['output-stopped'], state: { outputActive: false } }
                : { signals: [], state };
        case 'response.output_audio_transcript.done':
            return { signals: ['assistant-transcript-done'], state };
        case 'conversation.item.input_audio_transcription.completed':
            return { signals: ['user-transcript-done'], state };
        default:
            return { signals: [], state };
    }
}

function readEventType(data: unknown): string | null {
    if (typeof data !== 'string') return null;
    try {
        const type = JSON.parse(data)?.type;
        return typeof type === 'string' ? type : null;
    } catch {
        return null;
    }
}
